<script setup lang="ts">
import { CloseOutlined } from "@ant-design/icons-vue";
import type { ModalProps } from "ant-design-vue";
import { computed, useSlots } from "vue";

type CustomModalProps = Pick<
	ModalProps,
	| "onOk"
	| "onCancel"
	| "centered"
	| "width"
	| "title"
	| "zIndex"
	| "afterClose"
	| "cancelButtonProps"
	| "cancelText"
	| "okButtonProps"
	| "okText"
	| "okType"
	| "confirmLoading"
	| "bodyStyle"
>;

const props = withDefaults(defineProps<CustomModalProps>(), {
	width: 520,
	centered: false,
	zIndex: 1000,
	bodyStyle: () => ({
		padding: "20px 24px",
	}),
});

const hasTitleSlot = computed(() => !!useSlots().title);
const hasFooterSlot = computed(() => !!useSlots().footer);

const modelProps = computed(() => {
	const { title, ...rest } = props;

	const mp = Object.fromEntries(
		Object.entries(rest).filter(([_, v]) => v !== undefined)
	) as unknown as CustomModalProps;

	return {
		...mp,
	};
});

const open = defineModel("open", {
	type: Boolean,
	default: false,
});
</script>

<template>
	<a-modal v-model:open="open" :closable="false" v-bind="modelProps" wrap-class-name="custom-modal">
		<template #title>
			<div class="px-ml py-mm border-wireframe relative border-b border-solid">
				<template v-if="hasTitleSlot">
					<slot name="title"></slot>
				</template>

				<template v-else>
					{{ props.title }}
				</template>

				<div class="right-ml top-mm absolute cursor-pointer text-black" @click="open = false">
					<CloseOutlined />
				</div>
			</div>
		</template>

		<slot></slot>

		<template v-if="hasFooterSlot" #footer>
			<slot name="footer"></slot>
		</template>
	</a-modal>
</template>

<style lang="scss">
.custom-modal {
	.ant-modal-content {
		padding: 0;
	}

	.ant-modal-header {
		margin: 0;
	}

	.ant-modal-footer {
		margin: 0;
		padding: 0 20px 24px;
		margin-top: 12px;

		.ant-btn {
			min-width: 100px;
		}

		.ant-btn + .ant-btn:not(.ant-dropdown-trigger) {
			margin-inline-start: 12px;
		}
	}
}
</style>
